import {
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { SurveysState } from 'redux/types/surveys.types';
import { createApiThunk } from 'redux/utils/apiThunk';
import { surveyAdminRequests } from 'redux/requests/survey-admin.requests';
import { surveysRequests } from 'redux/requests/surveys.requests';
import { useAppSelector } from 'redux/storeHooks';
import { MemberSelectorParams } from './scheduledEventSlice.slice';
import { getFeedItems } from './feedSlice.slice';
import { SurveyV2 } from '@act/shared/data-transfer-objects';
import { SurveyEventTypes } from '@act/shared/socket-events';
import type { WebsocketEventActions } from '../types/websocketTypes';
import { getGoalsForMember, goalsTrackingActions } from './goalsTracking.slice';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';
import { FOLLOW_UP_SURVEY_KEY } from '@act/shared/constants';
import { getCurrentAndPendingHospitalizations } from './hospitalizations.slice';

// THUNKS/ACTIONS ==============================================================
export const updateAssignedSurvey = createApiThunk(
  surveysRequests.updateAssignedSurvey
);
export const answerQuestion = createApiThunk(
  surveysRequests.setQuestionResponse
);
export const updateAssignmentConfig = createApiThunk(
  surveyAdminRequests.updateAssignmentConfiguration
);

export const getAllSurveys = createApiThunk(surveysRequests.getAllSurveys);
export const getSurveyById = createApiThunk(surveysRequests.getSurveyById);
export const getStagesAndSegments = createApiThunk(
  surveyAdminRequests.getAllStagesAndSegments
);

export const updateAssignmentConfigSuccess = createAction<SurveyV2.SurveyDto>(
  'surveys/updateAssignmentConfigSuccess'
);

// INITIAL STATE ===============================================================
const assignedSurveysAdapter =
  createEntityAdapter<SurveyV2.AssignedSurveyDto>();
const surveysAdapter = createEntityAdapter<SurveyV2.SurveyDto>();

const initialState: SurveysState = {
  status: 'initial',
  assignedSurveys: assignedSurveysAdapter.getInitialState(),
  surveys: surveysAdapter.getInitialState(),
};

// SLICE =======================================================================
export const surveySlice = createSlice({
  name: 'surveys',
  initialState,
  reducers: {
    upsertAssignedSurvey: (
      state: SurveysState,
      action: PayloadAction<SurveyV2.AssignedSurveyDto>
    ) => {
      assignedSurveysAdapter.upsertOne(state.assignedSurveys, action.payload);
      return state;
    },
    /**
     * Merges note fields after debounced notes PATCH. Avoids replacing the full
     * assigned survey — the notes endpoint returns a shallow row without questions.
     */
    patchAssignedSurveyNotesFields: (
      state: SurveysState,
      action: PayloadAction<{
        id: number;
        notes: string;
        noteId?: number;
      }>
    ) => {
      const { id, notes, noteId } = action.payload;
      if (!state.assignedSurveys.entities[id]) {
        return state;
      }
      assignedSurveysAdapter.updateOne(state.assignedSurveys, {
        id,
        changes: {
          notes,
          ...(noteId !== undefined ? { noteId } : {}),
        },
      });
      return state;
    },
    upsertManyAssignedSurveys: (
      state: SurveysState,
      action: PayloadAction<SurveyV2.AssignedSurveyDto[]>
    ) => {
      assignedSurveysAdapter.upsertMany(state.assignedSurveys, action.payload);
      return state;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getFeedItems.fulfilled, (state, action) => {
        state.status = 'success';
        assignedSurveysAdapter.upsertMany(
          state.assignedSurveys,
          action.payload.assignedSurveys
        );
      })
      // Update assigned survey
      .addCase(updateAssignedSurvey.pending, state => {
        state.status = 'loading';
      })
      .addCase(updateAssignedSurvey.fulfilled, (state, action) => {
        state.status = 'success';
        assignedSurveysAdapter.upsertOne(state.assignedSurveys, action.payload);
      })
      .addCase(updateAssignedSurvey.rejected, state => {
        state.status = 'error';
      })

      // Answer question
      .addCase(answerQuestion.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        answerQuestion.fulfilled,
        (state, action: PayloadAction<SurveyV2.QuestionResponseViewDto>) => {
          state.status = 'success';
          assignedSurveysAdapter.upsertOne(
            state.assignedSurveys,
            action.payload.assignedSurvey
          );
        }
      )
      .addCase(answerQuestion.rejected, state => {
        state.status = 'error';
      })

      // Get all Survey configurations
      .addCase(getAllSurveys.pending, state => {
        state.status = 'loading';
        surveysAdapter.removeAll(state.surveys);
      })
      .addCase(getAllSurveys.fulfilled, (state, action) => {
        surveysAdapter.upsertMany(state.surveys, action.payload);
        state.status = 'success';
      })
      .addCase(getAllSurveys.rejected, state => {
        state.status = 'failed';
      })

      // Get single Survey configuration
      .addCase(getSurveyById.pending, state => {
        state.status = 'loading';
      })
      .addCase(getSurveyById.fulfilled, (state, action) => {
        surveysAdapter.upsertOne(state.surveys, action.payload);
        state.status = 'success';
      })
      .addCase(getSurveyById.rejected, state => {
        state.status = 'failed';
      })
      .addCase(updateAssignmentConfigSuccess, (state, action) => {
        surveysAdapter.upsertOne(state.surveys, action.payload);
        state.status = 'success';
      })
      .addCase(
        getCurrentAndPendingHospitalizations.fulfilled,
        (state, action) => {
          assignedSurveysAdapter.upsertMany(
            state.assignedSurveys,
            action.payload.assignedSurveys
          );
          state.status = 'success';
        }
      );
  },
});

// REDUCER =====================================================================
export const surveyReducer = surveySlice.reducer;
export const surveyActions = surveySlice.actions;

// SELECTORS ===================================================================
export const selectSurveysState = state =>
  (state as { surveys: typeof initialState })?.surveys.surveys;

export const selectAssignedSurveysAdapterState = state =>
  (state as { surveys: typeof initialState })?.surveys?.assignedSurveys;

const {
  selectAll: selectAssignedSurveys,
  selectById: _selectAssignedSurveyById,
} = assignedSurveysAdapter.getSelectors(selectAssignedSurveysAdapterState);

const { selectAll: selectAllSurveys, selectById: _selectSurveyById } =
  surveysAdapter.getSelectors(selectSurveysState);

export const selectAssignedSurveysByMember: (
  state,
  paramObj: MemberSelectorParams
) => SurveyV2.AssignedSurveyDto[] = createSelector(
  [
    selectAssignedSurveys,
    (state, paramObj: MemberSelectorParams) => paramObj.memberId,
  ],
  (assignedSurveys, memberId) =>
    assignedSurveys.filter(survey => survey.memberId === memberId)
);

export const selectHospitalizationSurveyByMember: (
  state,
  paramObj: MemberSelectorParams
) => SurveyV2.AssignedSurveyDto | undefined = createSelector(
  [
    selectAssignedSurveys,
    (state, paramObj: MemberSelectorParams) => paramObj.memberId,
  ],
  (assignedSurveys, memberId) =>
    assignedSurveys.find(
      survey =>
        survey.memberId === memberId &&
        survey.survey?.key === FOLLOW_UP_SURVEY_KEY
    )
);

// EXPORT HELPER ===============================================================
export const surveysSelectors = {
  useSelectAssignedSurveysByMember: (paramObj: MemberSelectorParams) =>
    useAppSelector(state => selectAssignedSurveysByMember(state, paramObj)),
  useSelectHospitalizationSurveyByMember: (paramObj: MemberSelectorParams) =>
    useAppSelector(state =>
      selectHospitalizationSurveyByMember(state, paramObj)
    ),
  useSelectAssignedSurveyById: (assignedSurveyId: number) => {
    return useAppSelector(state =>
      _selectAssignedSurveyById(state, assignedSurveyId)
    );
  },
  useSelectAllSurveys: () => useAppSelector(state => selectAllSurveys(state)),
  useSelectSurveyById: (surveyId: number) => {
    return useAppSelector(state => _selectSurveyById(state, surveyId));
  },
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: goalsTrackingActions.updateGoals,
    effect: async ({ dispatch, action }) => {
      if (action.payload) {
        void dispatch(
          surveyActions.upsertManyAssignedSurveys(
            action.payload.goalsTrackingView.assignedSurveys
          )
        );
      }
    },
  });
  addEffect({
    action: getGoalsForMember.fulfilled,
    effect: async ({ dispatch, action }) => {
      if (action.payload) {
        void dispatch(
          surveyActions.upsertManyAssignedSurveys(
            action.payload.assignedSurveys
          )
        );
      }
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
export const surveySliceWebsocketActions: WebsocketEventActions[] = [
  {
    type: SurveyEventTypes.SURVEYS_ASSIGNED,
    action: surveySlice.actions.upsertManyAssignedSurveys,
    channel: 'baseSocket',
  },
  {
    type: SurveyEventTypes.PHASE_COMPLETED,
    action: surveySlice.actions.upsertAssignedSurvey,
    channel: 'baseSocket',
  },
  {
    type: SurveyEventTypes.QUESTION_COMPLETED,
    action: surveySlice.actions.upsertAssignedSurvey,
    channel: 'baseSocket',
  },
  {
    type: SurveyEventTypes.SURVEY_UPDATED,
    action: surveySlice.actions.upsertAssignedSurvey,
    channel: 'baseSocket',
  },
];
